import { AlertCountryDTO } from 'src/app/core/models/system/alert/alertCountry.dto';
import { AlertCountryGroupDTO } from 'src/app/core/models/system/alert/alertCountryGroup.dto';
import { AlertDTO } from 'src/app/core/models/system/alert/alert.dto';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { firstValueFrom, forkJoin } from 'rxjs';
import { HttpClientService } from 'src/app/core/services/utelize/general/http-client-service';

@Component({
  selector: 'app-alert-country-coverage',
  templateUrl: './alert-country-coverage.component.html',
  styleUrls: ['./alert-country-coverage.component.scss']
})

export class AlertCountryCoverageComponent implements OnChanges {

  @Input() alert    : AlertDTO | undefined;
  @Input() alertId  : number | undefined;
  @Input() companyId: number | undefined;

  public activeTab     : 'countries' | 'groups' = 'countries';
  public countries     : AlertCountryDTO[]      = [];
  public groups        : AlertCountryGroupDTO[] = [];
  public query         : string                 = '';
  public loaded        : boolean                = false;

  public includedCount : number = 0;
  public excludedCount : number = 0;
  public effectiveCount: number = 0;

  public groupCoverage : Map<number, 'inc' | 'exc'> = new Map();

  public expandedGroups: Set<number>                  = new Set();
  private countryById  : Map<number, AlertCountryDTO> = new Map();

  public activeFilter  : 'inc' | 'exc' | 'eff' | null = null;
  private includedSet  : Set<number> = new Set();
  private excludedSet  : Set<number> = new Set();
  private effectiveSet : Set<number> = new Set();

  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService : ExceptionsService,
    private httpClientService : HttpClientService
  ) { }

  get allByDefault(): boolean {
    return this.alert?.allCountries == true;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['alert'] || changes['companyId'] || changes['alertId']) && this.alert != undefined && this.companyId != undefined) {
      this.load();
    }
  }

  public async load(): Promise<void> {
    try {
      if (this.alert == undefined || this.companyId == undefined)
        return;

      const { alertIdEncResult, companyIdEncResult, modifiedCountriesEncResult, modifiedGroupsEncResult } = await firstValueFrom(
        forkJoin({
          alertIdEncResult          : this.encryptionService.encrypt_AesServerSideForWeb((this.alertId ?? 0).toString()),
          companyIdEncResult        : this.encryptionService.encrypt_AesServerSideForWeb((this.companyId ?? -1).toString(), true),
          modifiedCountriesEncResult: this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(this.alert.alertCountrySelections ?? [])),
          modifiedGroupsEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(this.alert.alertCountryGroups ?? []))
        })
      );

      const alertIdEnc     = alertIdEncResult.result;
      const companyIdEncSS = companyIdEncResult.result;
      const allCountries   = this.allByDefault;

      const countriesUrl = environment.eSimApi + eSimApiPaths.Alerts + '/' + eSimApiPaths.AlertsGetAllCountriesForAlert +
                            '?alertIdEnc=' + alertIdEnc +
                            '&companyIdEncSS=' + companyIdEncSS +
                            '&allCountries=' + allCountries +
                            '&modifiedAlertCountryAllocations=' + modifiedCountriesEncResult.result;

      const groupsUrl    = environment.eSimApi + eSimApiPaths.CountryGroups + '/' + eSimApiPaths.CountryGroupsGetAllGroupsForAlert +
                            '?alertIdEnc=' + alertIdEnc +
                            '&companyIdEncSS=' + companyIdEncSS +
                            '&allCountries=' + allCountries +
                            '&modifiedAlertCountryGroupAllocations=' + modifiedGroupsEncResult.result;

      const { countries, groups } = await firstValueFrom(
        forkJoin({
          countries: this.httpClientService.get<AlertCountryDTO[]>(countriesUrl),
          groups   : this.httpClientService.get<AlertCountryGroupDTO[]>(groupsUrl)
        })
      );

      this.countries   = this.dedupeBy(countries ?? [], c => c.countryId);
      this.groups      = this.dedupeBy(groups ?? [], g => g.groupId).map(g => ({ ...g, countryIds: g.countryIds ?? [] }));
      this.countryById = new Map(this.countries.map(c => [c.countryId, c]));
      this.loaded      = true;

      this.recalc();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public switchTab(tab: 'countries' | 'groups') {
    this.activeTab = tab;
  }

  public toggleExpand(groupId: number) {
    if (this.expandedGroups.has(groupId))
      this.expandedGroups.delete(groupId);
    else
      this.expandedGroups.add(groupId);
  }

  public getGroupCountries(group: AlertCountryGroupDTO): AlertCountryDTO[] {
    return (group.countryIds ?? [])
      .map(id => this.countryById.get(id))
      .filter((c): c is AlertCountryDTO => c != null)
      .sort((a, b) => (a.countryName || a.alias || '').localeCompare(b.countryName || b.alias || ''));
  }

  public toggleDefault() {
    try {
      if (this.alert == undefined)
        return;

      this.alert.allCountries = !this.alert.allCountries;
      this.recalc();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public setCountry(country: AlertCountryDTO, state: 'inc' | 'exc') {
    try {
      if (state == 'inc') {
        const wasIncluded = country.included == true;
        country.included  = !wasIncluded;
        country.excluded  = false;
      } else {
        const wasExcluded = country.excluded == true;
        country.excluded  = !wasExcluded;
        country.included  = false;
      }
      country.changed = true;

      this.alert!.alertCountrySelections = this.countries.filter(c => c.changed == true);
      this.recalc();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public setGroup(group: AlertCountryGroupDTO, state: 'inc' | 'exc') {
    try {
      if (state == 'inc') {
        const wasIncluded = group.included == true;
        group.included    = !wasIncluded;
        group.excluded    = false;
      } else {
        const wasExcluded = group.excluded == true;
        group.excluded    = !wasExcluded;
        group.included    = false;
      }
      group.changed = true;

      this.alert!.alertCountryGroups = this.groups.filter(g => g.changed == true);
      this.recalc();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleFilter(filter: 'inc' | 'exc' | 'eff') {
    if (this.activeTab != 'countries') return;
    this.activeFilter = this.activeFilter == filter ? null : filter;
  }

  public clearFilter() {
    this.activeFilter = null;
  }

  public get filterLabel(): string {
    return this.activeFilter == 'inc' ? 'Included'
         : this.activeFilter == 'exc' ? 'Excluded'
         : this.activeFilter == 'eff' ? 'Effective Coverage'
         : '';
  }

  public get filteredCountries(): AlertCountryDTO[] {
    const q = this.query.toLowerCase().trim();
    let list = this.countries;

    if (q != '')
      list = list.filter(c => (c.countryName ?? '').toLowerCase().includes(q) ||
                              (c.alias ?? '').toLowerCase().includes(q) ||
                              (c.alpha2Code ?? '').toLowerCase().includes(q));

    if (this.activeFilter != null) {
      const set = this.activeFilter == 'inc' ? this.includedSet
                : this.activeFilter == 'exc' ? this.excludedSet
                : this.effectiveSet;
      list = list.filter(c => set.has(c.countryId));
    }

    return list;
  }

  public get filteredGroups(): AlertCountryGroupDTO[] {
    const q = this.query.toLowerCase().trim();
    if (q == '') return this.groups;
    return this.groups.filter(g => (g.groupName ?? '').toLowerCase().includes(q));
  }

  private dedupeBy<T>(items: T[], keyFn: (item: T) => number): T[] {
    const seen = new Set<number>();
    const result: T[] = [];
    for (const item of items) {
      const key = keyFn(item);
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  }

  private recalc() {
    try {
      const incSet = new Set<number>();
      const excSet = new Set<number>();

      this.countries.forEach(c => {
        if (c.included == true) incSet.add(c.countryId);
        if (c.excluded == true) excSet.add(c.countryId);
      });

      //Build per-country group coverage: included-via-group first, then excluded-via-group overrides (exclude wins)
      const coverage = new Map<number, 'inc' | 'exc'>();

      this.groups.forEach(g => {
        if (g.included == true) (g.countryIds ?? []).forEach(id => incSet.add(id));
        if (g.excluded == true) (g.countryIds ?? []).forEach(id => excSet.add(id));
      });

      this.groups.forEach(g => {
        if (g.included == true) (g.countryIds ?? []).forEach(id => { if (!coverage.has(id)) coverage.set(id, 'inc'); });
      });
      this.groups.forEach(g => {
        if (g.excluded == true) (g.countryIds ?? []).forEach(id => coverage.set(id, 'exc'));
      });
      this.groupCoverage = coverage;

      const base = new Set<number>(this.allByDefault ? this.countries.map(c => c.countryId) : []);
      incSet.forEach(id => base.add(id));
      excSet.forEach(id => base.delete(id));   //exclude always wins

      this.includedSet  = incSet;
      this.excludedSet  = excSet;
      this.effectiveSet = base;

      this.includedCount  = incSet.size;
      this.excludedCount  = excSet.size;
      this.effectiveCount = base.size;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
